// Workbench label helpers — external-facing labels and derived fields used
// by the preview Workbench routes. These wrap existing primitives from
// `lib/tracks` so the production Market/Client views remain untouched.
//
// External label policy (per user brief):
//  - "Market zone" instead of MacroTheme / Strategic theme
//  - "Market signal" instead of MarketTrack
//  - "Supporting pattern" instead of Sub-pattern
//  - "This week's movement" instead of Weekly Movement
//  - "Signal role" instead of Governance status

import {
    marketZoneForTrack,
    evidenceQualityLabelForTrack,
    momentumLabelForTrack,
    primaryRoleChipForTrack,
} from "../tracks";

/** Friendly external role label.
 *  Wraps `primaryRoleChipForTrack` but renames "Sub-pattern" to
 *  "Supporting pattern" and "Weekly movement" to "This week's movement"
 *  per the workbench wording brief. */
export const externalRoleLabel = (track) => {
    const chip = primaryRoleChipForTrack(track);
    if (!chip) return { label: "—", tone: "muted" };
    if (chip.label === "Sub-pattern") return { label: "Supporting pattern", tone: chip.tone };
    if (chip.label === "Weekly movement") return { label: "This week's movement", tone: chip.tone };
    return chip;
};

/** Market zone (or fallback to MacroTheme). */
export const marketZoneLabel = (track) => marketZoneForTrack(track) || "—";

/** Evidence quality label ("Broad evidence base", "Single-week evidence" …). */
export const evidenceLabel = (track) => evidenceQualityLabelForTrack(track) || "—";

/** Friendly "This week's movement" label — wraps `momentumLabelForTrack`
 *  and falls back to a humanised score-delta heuristic when no momentum
 *  signal is present. Returns null when truly nothing is known. */
export const thisWeekMovementLabel = (track, prev4WeekScore) => {
    const m = momentumLabelForTrack(track);
    if (m && m !== "No current weekly movement") return m;
    // Fallback: derive a simple direction from a 4-week delta when available.
    if (typeof track?.LatestScore === "number" && typeof prev4WeekScore === "number") {
        const delta = track.LatestScore - prev4WeekScore;
        if (delta >= 8) return "Strengthening";
        if (delta <= -8) return "Softening";
        if (Math.abs(delta) < 3) return "Stable";
        return "Mixed momentum";
    }
    return m || null;
};

/** 4-week score delta from history rows (oldest → newest). Returns null
 *  when fewer than 2 weeks of history are available. */
export const delta4w = (historyRows) => {
    if (!Array.isArray(historyRows) || historyRows.length === 0) return null;
    const sorted = historyRows.slice().sort((a, b) => (a.YearWeek || "").localeCompare(b.YearWeek || ""));
    const last = sorted[sorted.length - 1];
    const fourBack = sorted[Math.max(0, sorted.length - 5)] || sorted[0];
    const readScore = (r) => {
        if (!r) return null;
        if (typeof r.Score === "number") return r.Score;
        if (typeof r.WeeklyScore === "number") return r.WeeklyScore;
        if (typeof r.LatestScore === "number") return r.LatestScore;
        return null;
    };
    const ls = readScore(last);
    const fs = readScore(fourBack);
    if (ls === null || fs === null) return null;
    if (last === fourBack) return 0;
    return Math.round(ls - fs);
};

/** Format the 4-week delta as `+12` / `−4` / `0` (using a minus sign that
 *  reads cleanly in proportional fonts). */
export const formatDelta = (n) => {
    if (n === null || n === undefined) return "—";
    if (n === 0) return "0";
    return n > 0 ? `+${n}` : `\u2212${Math.abs(n)}`;
};

/** Tone for the delta chip. */
export const deltaTone = (n) => {
    if (n === null || n === undefined) return "muted";
    if (n >= 5) return "positive";
    if (n <= -5) return "negative";
    return "muted";
};

/** Best-effort "last seen" yearweek for a track. Prefers
 *  `asOfYearWeek`, falls back to the most recent history row's
 *  `YearWeek` field (per `/api/tracks/history` row shape). */
export const lastSeenYearWeek = (track, historyRows) => {
    if (track?.asOfYearWeek) return track.asOfYearWeek;
    if (Array.isArray(historyRows) && historyRows.length) {
        const sorted = historyRows.slice().sort((a, b) => (b.YearWeek || "").localeCompare(a.YearWeek || ""));
        return sorted[0]?.YearWeek || null;
    }
    return null;
};

/** Top-N companies as a compact display string. Reads
 *  `track.TopCompanies` first, then falls back to the latest week's
 *  `Companies` array from `/api/tracks/history`. */
export const topCompaniesLabel = (track, n = 3, historyRows) => {
    let arr = track?.TopCompanies || track?.latestWeeklyEvidence?.companies;
    if ((!Array.isArray(arr) || arr.length === 0) && Array.isArray(historyRows) && historyRows.length) {
        const sorted = historyRows.slice().sort((a, b) => (b.YearWeek || "").localeCompare(a.YearWeek || ""));
        const latest = sorted[0];
        if (latest && Array.isArray(latest.Companies)) arr = latest.Companies;
    }
    if (!Array.isArray(arr) || arr.length === 0) return null;
    const names = arr.slice(0, n).map((c) => (typeof c === "string" ? c : (c?.name || c?.Name))).filter(Boolean);
    if (!names.length) return null;
    const more = arr.length - names.length;
    return more > 0 ? `${names.join(", ")} +${more}` : names.join(", ");
};

/** Parent signal lookup — used by the "Group by parent signal" variant.
 *  Returns the parent track object when this row is a supporting pattern
 *  or weekly movement; returns the row itself when it's a top-level
 *  signal. Returns null only when no parent can be resolved. */
export const parentSignalOf = (track, allTracks) => {
    if (!track) return null;
    const role = track._role;
    if (role === "CORE_TRACK" || role === "WATCHLIST_TRACK") return track;
    const parentId = track?._governed?.parentTrackId || track.ParentTrackID || track.parentTrackId;
    if (!parentId || !Array.isArray(allTracks)) return null;
    return allTracks.find((t) => t.TrackID === parentId) || null;
};

/** Build groups for the grouped-by-parent variant. Returns an array of
 *  `{ key, parent, children: [...] }` where `children` includes the
 *  parent row as the FIRST entry; supporting patterns and weekly
 *  movements follow. Callers can sort `children.slice(1)` freely;
 *  the parent must always remain at index 0 so the hierarchy reads
 *  parent → supporting → weekly. */
export const groupByParent = (tracks) => {
    if (!Array.isArray(tracks)) return [];
    const byParent = new Map();
    const orphans = [];
    // Pass 1: seed groups from every top-level signal (parent first).
    for (const t of tracks) {
        const role = t._role;
        if (role === "CORE_TRACK" || role === "WATCHLIST_TRACK") {
            if (!byParent.has(t.TrackID)) byParent.set(t.TrackID, { key: t.TrackID, parent: t, children: [t] });
        }
    }
    // Pass 2: attach supporting / weekly children under their parent.
    for (const t of tracks) {
        const role = t._role;
        if (role === "CORE_TRACK" || role === "WATCHLIST_TRACK") continue;
        const parentId = t?._governed?.parentTrackId || t.ParentTrackID || t.parentTrackId;
        if (parentId && byParent.has(parentId)) {
            byParent.get(parentId).children.push(t);
        } else {
            orphans.push(t);
        }
    }
    const groups = Array.from(byParent.values());
    if (orphans.length) {
        groups.push({ key: "__orphans__", parent: null, children: orphans });
    }
    return groups;
};

/** Build groups for the grouped-by-zone variant. */
export const groupByZone = (tracks) => {
    if (!Array.isArray(tracks)) return [];
    const map = new Map();
    for (const t of tracks) {
        const zone = marketZoneLabel(t) || "Unassigned";
        if (!map.has(zone)) map.set(zone, { key: zone, parent: null, children: [] });
        map.get(zone).children.push(t);
    }
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
};
